import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Hexagon, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/app');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 honeycomb-bg opacity-20" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[100px]" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-sm"
      >
        <button
          data-testid="login-back-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-sm mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="relative">
            <Hexagon className="w-10 h-10 text-amber-500 fill-amber-500/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" />
            </div>
          </div>
          <div>
            <h1 className="font-heading font-black text-2xl text-white">Welcome back</h1>
            <p className="text-sm text-zinc-500">Log in to your Honey Pot account</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div data-testid="login-error" className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-zinc-400">Email</Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="bg-[#121214] border-white/10 text-white placeholder:text-zinc-600 h-11 focus:border-amber-500/50 focus:ring-amber-500/20"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-zinc-400">Password</Label>
            <div className="relative">
              <Input
                id="password"
                data-testid="login-password-input"
                type={showPw ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="bg-[#121214] border-white/10 text-white placeholder:text-zinc-600 h-11 pr-10 focus:border-amber-500/50 focus:ring-amber-500/20"
                required
              />
              <button
                type="button"
                data-testid="login-toggle-pw"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="honey-btn w-full py-3 rounded-xl text-sm font-bold disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="text-right">
            <button
              type="button"
              data-testid="login-forgot-password"
              onClick={() => navigate('/forgot-password')}
              className="text-xs text-zinc-500 hover:text-amber-400 transition-colors"
            >
              Forgot password?
            </button>
          </div>
        </form>

        <p className="text-center text-sm text-zinc-500 mt-6">
          Don't have an account?{' '}
          <button
            data-testid="login-to-signup"
            onClick={() => navigate('/signup')}
            className="text-amber-500 hover:text-amber-400 font-medium"
          >
            Sign up
          </button>
        </p>
      </motion.div>
    </div>
  );
}
